/**
 * WHICH source the opener's ACT / YES / NO cells are allowed to render, and
 * whether it is still fresh.
 *
 * One resolver for all three cells AND for the ACT-column sort, so a displayed
 * number and the rank it sorts under can never come from different sources.
 *
 * The rules it encodes:
 *  - The venue read is the only ACCOUNT-CORRECT source for a group. The REST
 *    `prediction_positions` map is synced MAIN-account-only, so it may stand in
 *    ONLY for a row we can prove belongs to the main account. A `testAccount`
 *    group, or a group whose account we have not resolved yet, renders `—`
 *    instead — never a main-account number.
 *  - The account is a property of each ROW's group, not of the page: the table
 *    mixes groups under "All groups", so the scope is resolved per row.
 *  - Freshness is aged in DURATIONS, never by subtracting a server timestamp
 *    from the browser clock, and the horizon comes off the wire (`staleMs`).
 *  - `ambiguous` counts belong to more than one pair and `null` counts are a
 *    hole in the read: both are "unknown", not a number.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  PairVenuePositions,
  VenuePositionsMeta,
  VenuePositionsMetaFrame,
  VenuePositionsSource
} from '../types/entry.types';

/**
 * Stale horizon used only when the backend does not send `staleMs`. Matches the
 * backend default (`STALE_MS_DEFAULT`) so an older pod degrades to the same
 * window it would have asked for.
 */
export const VENUE_STALE_MS_FALLBACK = 45_000;

/** Extra delay on the re-render scheduled at the stale boundary. */
const STALE_TICK_GRACE_MS = 250;

/** `setTimeout` truncates anything past this to "fire on the next tick". */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Delay for the re-render scheduled at the stale boundary.
 *
 * `setTimeout` truncates a delay past 2^31−1 to "fire now", which would re-arm
 * on every tick and SPIN the page instead of degrading it. `venueStaleMs`
 * already caps the horizon, so nothing currently reaches that bound — this is
 * the second lock on the same door, for when the ceiling is raised.
 */
export const staleTickDelayMs = (msLeft: number): number =>
  Math.min(msLeft + STALE_TICK_GRACE_MS, MAX_TIMEOUT_MS);

export type VenueUnavailableReason =
  | 'unconfigured'
  | 'blocked'
  | 'error'
  | 'starting'
  | 'stale'
  | 'missing'
  | 'ambiguous'
  | 'no-read'
  | 'link-down';

/**
 * How long the last venue counts may be HELD while this page has no socket.
 *
 * A dropped socket ages the read into `stale` even though the group's feed pod
 * may be sweeping normally — the heartbeat simply has no way in. On a scope
 * where REST may not stand in (a `testAccount` group, or one whose account is
 * unresolved) that turned every reconnect into the three cells flipping to `—`
 * and back, which reads as "the position vanished".
 *
 * Holding the group's OWN last counts, visibly marked, never crosses the line
 * the design actually protects: it is not another account's number. But it IS
 * an older one, so it is bounded — past this window "your counts are minutes
 * old" stops being information worth acting on and the cells go dark on the
 * ordinary `stale` path.
 */
export const VENUE_LINK_DOWN_HOLD_MS = 300_000;

/**
 * Which account a ROW trades under, as far as the page can prove: `unknown`
 * while the group list has not resolved that row's group yet.
 */
export type ViewAccountScope = 'main' | 'test' | 'unknown';

/** Everything the three cells render about the group-level venue read. */
export interface VenueViewStatus {
  /** The per-pair venue counts may be rendered. */
  fresh: boolean;
  /** Main-account REST numbers may stand in for this view. */
  restAllowed: boolean;
  account: 'prod' | 'test' | null;
  source: VenuePositionsSource | 'none';
  /**
   * Why the venue counts are not being rendered. Advisory in the two cases
   * where something IS rendered: with `restAllowed` it names what the REST
   * numbers are standing IN FOR (`stale` = this group's own feed pod stopped
   * sweeping), and `link-down` marks counts that ARE this group's own but are
   * being held while the page has no socket.
   */
  reason: VenueUnavailableReason | null;
  lastError: string | null;
  redeemableExcluded: number;
  /** ISO of the last successful sweep — set only while NOT fresh, where it is
   *  the one thing the unavailable (or stood-in-for) tooltip shows. Keeping the
   *  raw advancing timestamp out of the status is what makes its identity
   *  stable across the per-poll heartbeat. */
  lastSuccessLabel: string | null;
  scope: ViewAccountScope;
}

/**
 * Upper bound on the horizon we will honour off the wire. The backend clamps
 * `staleMs` too, so this is defence in depth against an older or misconfigured
 * pod: an unbounded horizon means the counts NEVER go stale, which is the same
 * money-losing direction as a frozen clock — a dead feed rendering as live.
 */
export const VENUE_STALE_MS_CEILING = 1_800_000;

export const venueStaleMs = (meta: VenuePositionsMeta): number =>
  typeof meta.staleMs === 'number' && meta.staleMs > 0
    ? Math.min(meta.staleMs, VENUE_STALE_MS_CEILING)
    : VENUE_STALE_MS_FALLBACK;

/**
 * Age of the last successful sweep, computed WITHOUT comparing the two clocks:
 * `serverEmittedAt − lastSuccessAt` is entirely in the server clock and the
 * time since the frame landed entirely in the browser's, so any constant skew
 * cancels.
 *
 * The elapsed-since-receipt term is read off the MONOTONIC clock
 * (`performance.now()`, `receivedMono`) rather than the wall clock: a wall
 * clock corrected BACKWARDS after the frame arrived makes `now - receivedAt`
 * negative, and clamping that at 0 would freeze the age — the counts would
 * read fresh for as long as the correction lasts even with a dead feed. A
 * monotonic clock cannot be corrected. `receivedMono` is absent only on a
 * frame built before this field existed, where the clamped wall-clock
 * difference stands in.
 *
 * Without `serverEmittedAt` (backend older than the `_meta` envelope) the first
 * term does mix clocks, but it is clamped at 0 — so a browser running behind
 * the pod can only lose the age accrued BEFORE the frame arrived, never keep a
 * dead feed looking fresh: the elapsed term grows on its own once the
 * heartbeat stops.
 */
export const venueAgeMs = (
  frame: VenuePositionsMetaFrame,
  now: number = Date.now(),
  mono: number = performance.now()
): number => {
  if (frame.meta.lastSuccessAt == null) return Number.POSITIVE_INFINITY;
  const anchor = frame.serverEmittedAt ?? frame.receivedAt;
  const ageAtFrame = Math.max(0, anchor - frame.meta.lastSuccessAt);
  const sinceFrame =
    frame.receivedMono == null
      ? Math.max(0, now - frame.receivedAt)
      : Math.max(0, mono - frame.receivedMono);
  return ageAtFrame + sinceFrame;
};

/** ms until the current read goes stale; `null` when there is nothing ageing. */
export const msUntilVenueStale = (
  frame: VenuePositionsMetaFrame | null | undefined,
  now: number = Date.now(),
  mono: number = performance.now()
): number | null => {
  if (!frame || frame.meta.source !== 'venue') return null;
  const age = venueAgeMs(frame, now, mono);
  if (!Number.isFinite(age)) return null;
  return venueStaleMs(frame.meta) - age;
};

/**
 * ms until the next moment the cells would render differently: the stale
 * boundary, or — once past it with the socket down — the end of the offline
 * hold. `null` when nothing is ageing towards a change.
 *
 * The hold boundary needs its own re-render for the same reason the stale one
 * does: nothing else schedules a render, so held counts would otherwise stay on
 * screen past the window that bounds them.
 */
export const msUntilVenueBoundary = (
  frame: VenuePositionsMetaFrame | null | undefined,
  linkDown = false,
  now: number = Date.now(),
  mono: number = performance.now()
): number | null => {
  if (!frame) return null;
  const untilStale = msUntilVenueStale(frame, now, mono);
  if (untilStale == null) return null;
  if (untilStale > 0) return untilStale;
  if (!linkDown) return null;
  const untilHoldEnds = VENUE_LINK_DOWN_HOLD_MS - venueAgeMs(frame, now, mono);
  return untilHoldEnds > 0 ? untilHoldEnds : null;
};

const isoOrNull = (at: number | null | undefined): string | null =>
  at == null ? null : new Date(at).toISOString();

/**
 * REST (`prediction_positions`) is synced MAIN-account-only, so it may stand in
 * only for a view we can PROVE is the main account: the page said so and the
 * pod (when there is one) did not contradict it.
 */
const mainAccountProven = (
  scope: ViewAccountScope,
  meta: VenuePositionsMeta | null
): boolean => scope === 'main' && meta?.account !== 'test';

export function resolveVenueStatus(
  frame: VenuePositionsMetaFrame | null | undefined,
  scope: ViewAccountScope,
  now: number = Date.now(),
  mono: number = performance.now(),
  linkDown = false
): VenueViewStatus {
  const meta = frame?.meta ?? null;
  const base = {
    account: meta?.account ?? null,
    source: meta?.source ?? ('none' as const),
    lastError: meta?.lastError ?? null,
    redeemableExcluded: meta?.redeemableExcluded ?? 0,
    scope
  };

  if (meta && frame) {
    if (meta.source === 'venue') {
      const age = venueAgeMs(frame, now, mono);
      const stale = age > venueStaleMs(meta);
      // A stale venue read on a group PROVEN to be the main account degrades
      // to the main-account REST number, which is the right answer for that
      // group — only a test (or unresolved) group has to go dark, because
      // there REST is a different account's position.
      // The degrade still REPORTS `stale`: it is the one signal that this
      // group's own feed pod stopped sweeping, and dropping it leaves an
      // ordinary REST cell with nothing to say so.
      const degradeToRest = stale && mainAccountProven(scope, meta);
      // Nothing above can tell "the pod stopped sweeping" from "we lost the
      // socket carrying its heartbeat" — but the page CAN: the socket is its
      // own. While it is down, and only on a scope with no REST to fall back
      // on, the group's own last counts are HELD (marked, and bounded by
      // VENUE_LINK_DOWN_HOLD_MS) rather than blanked on every reconnect.
      if (stale && !degradeToRest && linkDown && age <= VENUE_LINK_DOWN_HOLD_MS) {
        return {
          ...base,
          fresh: true,
          restAllowed: false,
          reason: 'link-down',
          lastSuccessLabel: isoOrNull(meta.lastSuccessAt)
        };
      }
      return {
        ...base,
        fresh: !stale,
        restAllowed: degradeToRest,
        reason: stale ? 'stale' : null,
        lastSuccessLabel: stale ? isoOrNull(meta.lastSuccessAt) : null
      };
    }
    // Any other source is a pod that HAS a venue to read and did not read it —
    // `unconfigured` / `blocked` / `error`, and `starting` (an armed feed pod
    // before its first successful sweep). None of them may borrow the REST
    // numbers, on any scope: a feed pod's group is not necessarily the main
    // account, and a group still coming up would otherwise show the main
    // account's position as its own for the whole boot window.
    if (meta.source !== 'off') {
      return {
        ...base,
        fresh: false,
        restAllowed: false,
        reason: meta.source,
        lastSuccessLabel: isoOrNull(meta.lastSuccessAt)
      };
    }
  }

  // No meta at all (core gateway), or a pod reporting `off` — the CORE pod,
  // which holds no venue credentials at all, so main-account REST is the
  // legitimate answer there.
  const proven = mainAccountProven(scope, meta);
  return {
    ...base,
    fresh: false,
    restAllowed: proven,
    reason: proven ? null : 'no-read',
    lastSuccessLabel: proven ? null : isoOrNull(meta?.lastSuccessAt)
  };
}

/**
 * A main-account group whose own venue read went stale falls back to the REST
 * numbers, which is the account-correct answer — but this note is the only
 * place the operator would ever learn that the group's feed pod STOPPED
 * SWEEPING, so the cells say so instead of passing for an ordinary REST read.
 */
export const restStaleVenueNote = (status: VenueViewStatus): string =>
  status.restAllowed && status.reason === 'stale'
    ? ' · This group’s own venue read is STALE (last success ' +
      `${status.lastSuccessLabel ?? 'unknown'}) — its feed pod stopped ` +
      'sweeping, so these are the MAIN-account REST numbers standing in.'
    : '';

/** Field-by-field equality over everything the cells render. */
export const venueStatusEqual = (
  a: VenueViewStatus,
  b: VenueViewStatus
): boolean =>
  a.fresh === b.fresh &&
  a.restAllowed === b.restAllowed &&
  a.account === b.account &&
  a.source === b.source &&
  a.reason === b.reason &&
  a.lastError === b.lastError &&
  a.redeemableExcluded === b.redeemableExcluded &&
  a.lastSuccessLabel === b.lastSuccessLabel &&
  a.scope === b.scope;

export type PairVenueView =
  | {
      kind: 'venue';
      venue: PairVenuePositions;
      account: 'prod' | 'test';
      resolving: boolean;
    }
  | { kind: 'rest' }
  | { kind: 'unavailable'; reason: VenueUnavailableReason };

/**
 * A count that is not a finite number is a hole in the read, not a position:
 * rendering it would print `NaN`, and ranking it would make the ACT
 * comparator inconsistent (`NaN - x` is `NaN`, so the sort order becomes
 * implementation-defined).
 */
const countsUsable = (v: PairVenuePositions): boolean =>
  Number.isFinite(v.polyYes) &&
  Number.isFinite(v.polyNo) &&
  Number.isFinite(v.kalshiYes) &&
  Number.isFinite(v.kalshiNo);

/** The source ONE row may render, given the group status and its own counts. */
export function resolvePairVenueView(
  status: VenueViewStatus,
  venue: PairVenuePositions | null | undefined
): PairVenueView {
  if (status.fresh) {
    if (!venue) return { kind: 'unavailable', reason: 'missing' };
    if (venue.ambiguous) return { kind: 'unavailable', reason: 'ambiguous' };
    if (!countsUsable(venue)) return { kind: 'unavailable', reason: 'missing' };
    return {
      kind: 'venue',
      venue,
      account: status.account ?? 'prod',
      resolving: venue.resolving === true
    };
  }
  if (status.restAllowed) return { kind: 'rest' };
  return { kind: 'unavailable', reason: status.reason ?? 'no-read' };
}

/**
 * Signed `YES − NO` from the venue-polled per-market counts, same excess-YES
 * convention as the REST and believed-position nets.
 */
export const venueSignedNet = (v: PairVenuePositions): number =>
  v.polyYes + v.kalshiYes - (v.polyNo + v.kalshiNo);

/**
 * Sort magnitude for the ACT column, taken from the SAME view the cell renders
 * — so a row can never be ranked by a source it is not displaying. `Infinity`
 * means "unknown", so those rows lead the descending (largest-naked-first)
 * order instead of tying with the flat majority.
 *
 * The one deliberate divergence between rank and display: a `resolving` pair
 * SHOWS its signed net but RANKS flat, because that imbalance is a settled leg
 * awaiting redemption, not an open naked leg to hedge. It is the same number
 * either way, read for two different questions.
 */
export const venueSortMagnitude = (
  view: PairVenueView,
  restMagnitude: number
): number => {
  if (view.kind === 'venue') {
    if (view.resolving) return 0;
    const mag = Math.abs(venueSignedNet(view.venue));
    return Number.isFinite(mag) ? mag : Number.POSITIVE_INFINITY;
  }
  if (view.kind === 'rest') {
    return Number.isFinite(restMagnitude)
      ? restMagnitude
      : Number.POSITIVE_INFINITY;
  }
  return Number.POSITIVE_INFINITY;
};

/**
 * Re-render this component when the current read crosses its stale boundary.
 * Staleness is otherwise only ever evaluated during a render nothing
 * schedules, so a table that stopped receiving store notifications would keep
 * showing venue counts past the horizon forever.
 */
function useVenueStaleTick(
  frame: VenuePositionsMetaFrame | null | undefined,
  linkDown: boolean
): void {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const left = msUntilVenueBoundary(frame, linkDown);
    if (left == null || !Number.isFinite(left) || left <= 0) return;
    const timer = setTimeout(() => setTick(n => n + 1), staleTickDelayMs(left));
    return () => clearTimeout(timer);
  }, [frame, linkDown, tick]);

  // Browsers throttle a hidden tab's timers to ~1/min, so the boundary
  // re-render can arrive minutes late — long enough for a tab left open on a
  // dead feed to still be showing venue counts when the operator returns to
  // it. Re-evaluate on the way back in.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibilityChange = () => {
      if (!document.hidden) setTick(n => n + 1);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);
}

/**
 * Hold ONE object per distinct status: a new identity only when something the
 * cells render changes, so the per-poll heartbeat (`lastSuccessAt` advances
 * every ~10s) no longer re-sorts the table and re-renders every row.
 */
function useStableVenueStatus(next: VenueViewStatus): VenueViewStatus {
  const held = useRef<VenueViewStatus | null>(null);
  if (!held.current || !venueStatusEqual(held.current, next)) {
    held.current = next;
  }
  return held.current;
}

/** The group venue status resolved for every account scope a row can be in. */
export type VenueStatusByScope = Record<ViewAccountScope, VenueViewStatus>;

/**
 * One status per account scope, each identity-stable.
 *
 * Resolved per SCOPE rather than once per page because the table is not
 * single-account: under "All groups" it mixes pairs from main-account and
 * `testAccount` groups, and the answer to "may this row show a REST number"
 * differs between them. Each row then picks the status of its OWN group.
 */
export function useVenuePositionsStatusByScope(
  frame: VenuePositionsMetaFrame | null | undefined,
  linkDown = false
): VenueStatusByScope {
  useVenueStaleTick(frame, linkDown);
  const now = Date.now();
  const mono = performance.now();
  const main = useStableVenueStatus(
    resolveVenueStatus(frame, 'main', now, mono, linkDown)
  );
  const test = useStableVenueStatus(
    resolveVenueStatus(frame, 'test', now, mono, linkDown)
  );
  const unknown = useStableVenueStatus(
    resolveVenueStatus(frame, 'unknown', now, mono, linkDown)
  );
  return useMemo(
    () => ({ main, test, unknown }),
    [main, test, unknown]
  );
}

/** The fields of an opener group the account scope is derived from. */
export interface GroupAccountRow {
  _id: string;
  testAccount?: boolean;
}

/**
 * A group list together with the moment its fetch was ISSUED — the cached
 * `opener-groups` value (`OpenerGroupList`) satisfies this. Taken as one value
 * so the rows and the clock they are judged by cannot come from two different
 * fetches.
 */
export interface GroupAccountSource {
  groups: GroupAccountRow[];
  fetchStartedAt: number;
}

/** Resolved account scope per opener group; absent = not resolved. */
export interface GroupAccountIndex {
  byGroup: ReadonlyMap<string, ViewAccountScope>;
}

/**
 * Which account each opener group trades under, as far as the page can PROVE.
 *
 * `testAccount` is chosen at START time and is only readable from the
 * `opener-groups` row, which polls every 30s — so a group started against the
 * test account keeps reporting the PREVIOUS run's account until that row is
 * re-read. `unresolvedMarks` carries the moment each group's account was last
 * invalidated (a start/stop); until the group list has been fetched AFTER that
 * moment the group is left out of the index entirely, which resolves it to
 * `unknown` rather than to a `main` we cannot stand behind.
 *
 * The clock compared against the mark is when the HELD list's fetch STARTED,
 * never when it completed: a fetch already in flight when the mark is written
 * carries pre-start rows, and completing it later would clear the guard with
 * exactly the answer the mark exists to distrust.
 *
 * Equal timestamps are treated as unresolved. Both clocks are `Date.now()`, so
 * one millisecond cannot order them, and a fetch that left within the mark's
 * millisecond is as likely to predate it as to follow it. Staying `unknown` for
 * one more poll costs a dark cell; guessing costs the wrong account's numbers.
 */
export function buildGroupAccountIndex(
  list: GroupAccountSource | null | undefined,
  unresolvedMarks: ReadonlyMap<string, number>
): GroupAccountIndex {
  const byGroup = new Map<string, ViewAccountScope>();
  if (!list) return { byGroup };
  for (const group of list.groups) {
    const markedAt = unresolvedMarks.get(group._id);
    if (markedAt != null && list.fetchStartedAt <= markedAt) continue;
    byGroup.set(group._id, group.testAccount === true ? 'test' : 'main');
  }
  return { byGroup };
}

/**
 * Account scope of ONE row, from the pair's own group — NOT from the page
 * selection, which cannot express "this row belongs to a test group" while the
 * table is showing All groups / Ungrouped.
 *
 * A pair in no group is traded by the always-on core engine, which holds the
 * main account's credentials, so it is provably `main`.
 */
export function rowAccountScope(
  openerGroupId: string | null | undefined,
  index: GroupAccountIndex
): ViewAccountScope {
  if (!openerGroupId) return 'main';
  return index.byGroup.get(openerGroupId) ?? 'unknown';
}
